/**
 * Modal, gaveta e o ⓘ "Como ler esta página" (spec 04-TELAS/0.3 · spec 05 §16).
 *
 * Os três são a mesma coisa por dentro — conteúdo sobre um véu — e por isso
 * compartilham o comportamento que ninguém lembra de implementar duas vezes igual:
 * Esc fecha, clicar fora fecha, o corpo para de rolar e o foco vai para o botão de
 * fechar. Um modal sem trava de rolagem parece quebrado no celular.
 *
 * ## O que o lote U1 acrescentou, e por quê (achado A11Y-01)
 *
 * O diálogo tinha `aria-modal="true"` e **não era modal**. `aria-modal` é uma promessa
 * ao leitor de tela; quem a cumpre é o navegador, e para isso o resto da página precisa
 * estar de fato inalcançável. Não estava: um `Tab` a partir do último botão do diálogo
 * seguia para a página de trás — o usuário de teclado saía do modal sem saber, ia
 * preenchendo campos que não via, e o leitor de tela anunciava conteúdo que a tela
 * mostrava embaçado atrás do véu.
 *
 * São três peças, e nenhuma sozinha resolve:
 *
 *  1. **`inert` no fundo.** Tudo que é irmão do diálogo em `document.body` fica inerte
 *     enquanto ele está aberto: sai da ordem de tabulação, do alcance do ponteiro e da
 *     árvore de acessibilidade, de uma vez. É o que `aria-modal` já prometia.
 *  2. **Sentinelas.** `inert` tira o fundo do caminho, mas o foco ainda pode ir para a
 *     barra do navegador e voltar pelo começo do documento. Dois nós focalizáveis, um
 *     antes e um depois do conteúdo, devolvem o foco para a outra ponta — o ciclo
 *     fecha dentro do diálogo.
 *  3. **Portal + pilha.** O diálogo precisa ser IRMÃO do `#root`, não filho: fosse
 *     filho, marcar o fundo como inerte marcaria o próprio diálogo. E como diálogo
 *     abre diálogo (a ficha abre a baixa), cada um marca o anterior e desfaz **apenas
 *     o que ele mesmo marcou** ao fechar.
 *
 * E o fechamento devolve o foco a quem abriu. Sem isso, fechar o modal joga o foco para
 * o começo do documento, e quem navega por teclado recomeça a página inteira.
 *
 * `Sobreposicao` é a peça sem aparência: quem já tem marcação própria — o formulário de
 * Tarefas, a busca global — adota o comportamento sem trocar um pixel.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Link } from '@tanstack/react-router'
import { Icone } from '../shell/icone.tsx'
import { useTextos } from '../textos.ts'
import { DicaContextual } from './dica-contextual.tsx'
import { ContextoDaLinha } from './linha-da-listagem-contexto.ts'
import { TEXTOS_DA_SOBREPOSICAO } from './sobreposicao-textos.ts'
import { MARCA_DAS_ACOES_DO_DIALOGO } from './toast-regras.ts'

const FOCALIZAVEIS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function focalizaveisDe(raiz: HTMLElement): HTMLElement[] {
  return Array.from(raiz.querySelectorAll<HTMLElement>(FOCALIZAVEIS)).filter(
    (elemento) =>
      elemento.dataset['sentinela'] === undefined &&
      elemento.offsetParent !== null &&
      elemento.getAttribute('aria-hidden') !== 'true',
  )
}

/**
 * A pilha de sobreposições abertas, e o isolamento derivado dela.
 *
 * A primeira versão era mais simples — cada diálogo marcava os irmãos ao abrir e
 * desmarcava ao fechar — e estava errada de um jeito que só um diálogo SOBRE outro
 * revela: a gaveta de entidades ficava aberta, o modal de reset abria dentro dela, e a
 * gaveta reexecutava o efeito no render seguinte (o `aoFechar` dela é uma seta criada a
 * cada render do pai). Ao reexecutar, ela marcava como inerte "tudo que não sou eu" — e
 * "tudo" agora incluía o modal recém-aberto. O campo "Digite APAGAR SANDBOX" ficava
 * inerte: aceitava o clique do Playwright sem erro e não recebia uma letra.
 *
 * O isolamento passou a ser DERIVADO da pilha, e não acumulado por quem chegou: inerte é
 * todo irmão de `document.body` que não seja o TOPO. Assim a resposta não depende de
 * quantas vezes cada efeito rodou nem em que ordem — só de quem está por cima agora.
 *
 * `marcadosPorNos` existe para não desfazer o que não foi obra nossa: um `inert` posto
 * por outra biblioteca continua onde estava.
 */
const pilhaDeSobreposicoes: HTMLElement[] = []
const marcadosPorNos = new Set<HTMLElement>()
const observadoresDaPilha = new Set<() => void>()
const observarPilha = (avisar: () => void): (() => void) => {
  observadoresDaPilha.add(avisar)
  return () => { observadoresDaPilha.delete(avisar) }
}
const topoDaPilha = (): HTMLElement | null => pilhaDeSobreposicoes.at(-1) ?? null

/**
 * Há diálogo aberto? (PK-02) O Ctrl+K pergunta isto: com um diálogo na tela ele não abre a
 * Busca por baixo — um diálogo não é "tela" (spec 2.2:54) — e o atalho é só consumido.
 */
export const haSobreposicaoAberta = (): boolean => pilhaDeSobreposicoes.length > 0

/** Avisos da ação ficam no diálogo ativo, participando do isolamento e do foco. */
export function useDestinoDosAvisos(): HTMLElement | null {
  return useSyncExternalStore(observarPilha, topoDaPilha, () => null)
}

function aplicarIsolamento(): void {
  for (const antigo of marcadosPorNos) {
    if (!antigo.isConnected) marcadosPorNos.delete(antigo)
  }
  const topo = pilhaDeSobreposicoes[pilhaDeSobreposicoes.length - 1]
  for (const irmao of Array.from(document.body.children)) {
    if (!(irmao instanceof HTMLElement)) continue
    const deveSerInerte = topo !== undefined && irmao !== topo
    if (deveSerInerte) {
      if (!irmao.inert) {
        irmao.inert = true
        marcadosPorNos.add(irmao)
      }
    } else if (marcadosPorNos.has(irmao)) {
      irmao.inert = false
      marcadosPorNos.delete(irmao)
    }
  }
}

export function Sobreposicao({
  aberto,
  aoFechar,
  rotulo,
  rotuladoPor,
  className,
  aoAbrirFocar,
  aoPressionarFora,
  children,
}: {
  aberto: boolean
  aoFechar: () => void
  /** Um dos dois é obrigatório na prática: diálogo sem nome não se anuncia. */
  rotulo?: string
  rotuladoPor?: string
  className: string
  /**
   * Para onde o foco vai ao abrir. Sem isto, vai para o primeiro focalizável.
   * O tipo é a FORMA da ref, não `RefObject<HTMLElement>`: assim uma
   * `RefObject<HTMLInputElement>` serve sem cast.
   */
  aoAbrirFocar?: { readonly current: HTMLElement | null }
  /** Para quem desenha o próprio véu e fecha ao pressionar fora do painel. */
  aoPressionarFora?: () => void
  children: ReactNode
}) {
  const container = useRef<HTMLDivElement>(null)

  /*
    `aoFechar` numa referência, e o efeito dependendo só de `aberto`.

    Quem chama passa uma seta criada no corpo do componente, então a identidade da
    função muda a cada render do pai. Com ela na lista de dependências, o efeito
    reexecutava a cada tecla digitada dentro do diálogo — e reexecutar significa
    devolver o foco ao elemento inicial. Digitar no segundo campo era impossível: o
    cursor voltava ao "X" a cada letra.
  */
  const fechar = useRef(aoFechar)
  fechar.current = aoFechar
  const focoInicial = useRef(aoAbrirFocar)
  focoInicial.current = aoAbrirFocar

  useEffect(() => {
    if (!aberto) return
    const alvo = container.current
    if (alvo === null) return

    // Quem abriu. O foco volta para cá no fechamento — inclusive quando o próprio ato
    // desmonta o botão, caso em que `isConnected` é falso e não se força nada.
    const acionador = document.activeElement

    pilhaDeSobreposicoes.push(alvo)
    aplicarIsolamento()
    for (const avisar of observadoresDaPilha) avisar()
    const rolagemAnterior = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    // Esc fecha só o TOPO da pilha (PK-02). Cada sobreposição escuta o documento; sem esta
    // pergunta, um Esc na modal aberta sobre a gaveta fechava as duas de uma vez.
    const porTecla = (evento: KeyboardEvent): void => {
      if (evento.key === 'Escape' && topoDaPilha() === alvo) fechar.current()
    }
    document.addEventListener('keydown', porTecla)

    const inicial = focoInicial.current?.current ?? focalizaveisDe(alvo)[0]
    inicial?.focus()

    return () => {
      document.removeEventListener('keydown', porTecla)
      const posicao = pilhaDeSobreposicoes.indexOf(alvo)
      if (posicao !== -1) pilhaDeSobreposicoes.splice(posicao, 1)
      aplicarIsolamento()
      for (const avisar of observadoresDaPilha) avisar()
      // A rolagem só volta quando não sobrou diálogo nenhum: fechar o de cima com a
      // gaveta ainda aberta não pode devolver a rolagem ao fundo.
      if (pilhaDeSobreposicoes.length === 0) document.body.style.overflow = rolagemAnterior
      if (acionador instanceof HTMLElement && acionador.isConnected) acionador.focus()
    }
  }, [aberto])

  const paraOFim = useCallback(() => {
    const focalizaveis = container.current === null ? [] : focalizaveisDe(container.current)
    focalizaveis[focalizaveis.length - 1]?.focus()
  }, [])

  const paraOInicio = useCallback(() => {
    const focalizaveis = container.current === null ? [] : focalizaveisDe(container.current)
    focalizaveis[0]?.focus()
  }, [])

  if (!aberto) return null

  return createPortal(
    <div
      ref={container}
      className={className}
      role="dialog"
      aria-modal="true"
      {...(aoPressionarFora === undefined ? {} : { onMouseDown: aoPressionarFora })}
      {...(rotulo === undefined ? {} : { 'aria-label': rotulo })}
      {...(rotuladoPor === undefined ? {} : { 'aria-labelledby': rotuladoPor })}
    >
      {/*
        Sentinelas. `position: fixed` para sair do fluxo: dentro de um container flex
        elas seriam itens de largura zero, e este arquivo é comparado pixel a pixel
        contra o protótipo.
      */}
      <span data-sentinela="inicio" tabIndex={0} onFocus={paraOFim} style={{ position: 'fixed' }} />
      {children}
      <span data-sentinela="fim" tabIndex={0} onFocus={paraOInicio} style={{ position: 'fixed' }} />
    </div>,
    document.body,
  )
}

/**
 * O clique no véu fecha, mas não o SEGUNDO clique de um duplo clique (relato G1 · D-155 ·
 * DS-569): a linha de listagem abre a gaveta ou o modal no primeiro clique, e o segundo cairia
 * no véu recém-aberto e fecharia o que acabou de abrir. Um clique de verdade tem `detail` 1.
 */
function fecharPeloVeu(aoFechar: () => void): (evento: ReactMouseEvent<HTMLDivElement>) => void {
  return (evento) => {
    if (evento.detail > 1) return
    aoFechar()
  }
}

export function Modal({
  aberto,
  titulo,
  aoFechar,
  children,
  rodape,
  largura = 'max-w-lg',
  largo = false,
  rodapeFixo = false,
  protegerRodape = false,
  ajuda,
}: {
  aberto: boolean
  titulo: string
  aoFechar: () => void
  children: ReactNode
  rodape?: ReactNode
  largura?: string
  largo?: boolean
  /**
   * A dica do diálogo (D-203): a explicação de como ele funciona, num ícone ao lado do título.
   * Existe porque, com o diálogo aberto, o ⓘ da página fica inerte: o que o diálogo explica mora
   * nele. O que a pessoa precisa saber para não errar continua no corpo, visível.
   */
  ajuda?: ReactNode
  /**
   * O rodapé de ações fica colado no fim do painel enquanto o conteúdo rola (PK-08). É
   * OPCIONAL de propósito: vale para o formulário que cresce com a lista (a Nova campanha
   * com muitos itens), e não muda nenhum modal que não o peça.
   */
  rodapeFixo?: boolean
  /**
   * Os avisos (toasts) que surgem com o diálogo aberto sobem para acima dos controles do rodapé
   * quando os cobririam (FX2 da v1.8.0, DS-654 (t); a regra em `toast-regras.ts`). No celular o
   * diálogo alto põe o rodapé no chão da tela, embaixo da pilha, e o aviso de validação deitava em
   * cima do "Salvar". OPCIONAL, como o `rodapeFixo`: não muda nenhum modal que não o peça.
   */
  protegerRodape?: boolean
}) {
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const fechar = useRef<HTMLButtonElement>(null)

  return (
    // Um diálogo é painel de detalhe: mesmo aberto de dentro de uma linha de listagem, o nome
    // de registro nele volta a ser link (D-155).
    <ContextoDaLinha.Provider value={false}>
      <Sobreposicao
        aberto={aberto}
        aoFechar={aoFechar}
        rotulo={titulo}
        aoAbrirFocar={fechar}
        className="fixed inset-0 z-[75]"
      >
        <div className="absolute inset-0 bg-[var(--dv-veu)]" onClick={fecharPeloVeu(aoFechar)} aria-hidden="true" data-teste="veu" />
        {/*
          A LARGURA PEDIDA é a efetiva (PK-02). A base fixava 560 px e a prop `largura` só
          conseguia estreitar: quem pedia `max-w-2xl` ou `max-w-3xl` recebia 560. Agora a base é a
          largura da viewport e quem limita é o `max-w` pedido — o padrão continua 512 px
          (`max-w-lg`), quem estreita segue igual, e `largo` / `!w-[…]` não mudam.
        */}
        <div
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[92vw] ${largo ? '!w-[min(92vw,1024px)] max-w-5xl' : largura} max-h-[85vh] overflow-y-auto bg-white rounded-2xl border border-hairline shadow-card p-6`}
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            {ajuda === undefined ? (
              <h2 className="text-[17px] font-semibold text-navy">{titulo}</h2>
            ) : (
              <div className="flex min-w-0 items-center gap-1">
                <h2 className="text-[17px] font-semibold text-navy">{titulo}</h2>
                <DicaContextual rotulo={titulo}>{ajuda}</DicaContextual>
              </div>
            )}
            <button
              ref={fechar}
              type="button"
              onClick={aoFechar}
              aria-label={texto('fechar')}
              /*
                Marca própria porque "o botão Fechar do diálogo" não é localizável pelo
                NOME: a pilha de toasts é portada para DENTRO do diálogo (`useDestinoDosAvisos`,
                e ela precisa estar aí, senão o `<dialog>` modal a deixa inerte), e cada toast
                tem o seu próprio "Fechar". Enquanto os toasts vivem — seis segundos — procurar
                por nome acha três botões e a busca falha por ambiguidade, não por defeito.
              */
              data-teste="fechar-modal"
              className="w-8 h-8 rounded-full grid place-items-center text-faint hover:text-navy hover:bg-navy/5"
            >
              <Icone nome="x" className="w-4 h-4" />
            </button>
          </div>
          {children}
          {rodape !== undefined && (
            <div
              className={rodapeFixo
                ? 'sticky -bottom-6 z-10 -mx-6 -mb-6 mt-6 flex justify-end gap-2 rounded-b-2xl border-t border-hairline bg-white px-6 py-4'
                : 'flex justify-end gap-2 mt-6'}
              data-teste={rodapeFixo ? 'rodape-fixo' : undefined}
              {...(protegerRodape ? { [MARCA_DAS_ACOES_DO_DIALOGO]: '' } : {})}
            >
              {rodape}
            </div>
          )}
        </div>
      </Sobreposicao>
    </ContextoDaLinha.Provider>
  )
}

/** Gaveta à direita — a ficha da 10.2 e o ⓘ da 2.4 moram aqui. */
export function Gaveta({
  aberta,
  titulo,
  aoFechar,
  children,
  rodape,
  largura = 'max-w-md',
  ajuda,
}: {
  aberta: boolean
  titulo: string
  aoFechar: () => void
  children: ReactNode
  rodape?: ReactNode
  /** A dica da gaveta (D-203), ao lado do título; ver o `Modal`. */
  ajuda?: ReactNode
  /**
   * A largura máxima da gaveta. O padrão serve para ficha e formulário curto; conteúdo
   * tabular — o extrato bancário, por exemplo — sufoca nele: histórico, contraparte,
   * data, documento e valor competem pela mesma linha de 448px e tudo vira reticências.
   */
  largura?: string
}) {
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const fechar = useRef<HTMLButtonElement>(null)

  return (
    // A gaveta é painel de detalhe: o nome de registro nela é link, como no Modal (D-155).
    <ContextoDaLinha.Provider value={false}>
      <Sobreposicao
        aberto={aberta}
        aoFechar={aoFechar}
        rotulo={titulo}
        aoAbrirFocar={fechar}
        className="fixed inset-0 z-[70]"
      >
        <div className="absolute inset-0 bg-[var(--dv-veu)]" onClick={fecharPeloVeu(aoFechar)} aria-hidden="true" data-teste="veu" />
        <aside
          data-teste="gaveta"
          className={`absolute right-0 inset-y-0 w-full ${largura} bg-paper border-l border-hairline shadow-card flex flex-col`}
        >
          <header className="h-16 shrink-0 flex items-center justify-between gap-3 px-5 border-b border-hairline bg-white">
            {ajuda === undefined ? (
              <h2 className="text-base font-semibold text-navy truncate">{titulo}</h2>
            ) : (
              <div className="flex min-w-0 items-center gap-1">
                <h2 className="text-base font-semibold text-navy truncate">{titulo}</h2>
                <DicaContextual rotulo={titulo}>{ajuda}</DicaContextual>
              </div>
            )}
            <button
              ref={fechar}
              type="button"
              onClick={aoFechar}
              aria-label={texto('fechar')}
              /* Mesma razão do Modal: a pilha de toasts mora dentro da sobreposição. */
              data-teste="fechar-gaveta"
              className="w-8 h-8 rounded-full grid place-items-center text-faint hover:text-navy hover:bg-navy/5 shrink-0"
            >
              <Icone nome="x" className="w-4 h-4" />
            </button>
          </header>
          <div className="flex-1 overflow-y-auto p-5 space-y-5">{children}</div>
          {rodape !== undefined && (
            <footer className="shrink-0 border-t border-hairline bg-white p-4 flex justify-end gap-2">{rodape}</footer>
          )}
        </aside>
      </Sobreposicao>
    </ContextoDaLinha.Provider>
  )
}

export interface SecaoDoInfo {
  readonly titulo: string
  readonly conteudo: ReactNode
}

/**
 * O ⓘ "Como ler esta página" — três seções canônicas (spec 05 §16).
 *
 * Existe para que a tela não precise explicar-se no meio do conteúdo: explicação
 * longa vive AQUI, e a página fica com rótulo e número.
 */
export function BotaoDeInfo({ secoes }: { secoes: readonly SecaoDoInfo[] }) {
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const [aberto, setAberto] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => {
          setAberto(true)
        }}
        aria-label={texto('ler')}
        title={texto('ler')}
        className="w-8 h-8 rounded-full border border-hairline bg-white grid place-items-center text-muted hover:text-navy hover:border-navy/40 shrink-0 align-middle"
      >
        <Icone nome="info" className="w-4 h-4" />
      </button>
      <Modal
        aberto={aberto}
        titulo={texto('ler')}
        aoFechar={() => {
          setAberto(false)
        }}
      >
        <div className="space-y-5 text-sm">
          {secoes.map((secao) => (
            <section key={secao.titulo}>
              <p className="eyebrow text-muted mb-2">{secao.titulo}</p>
              <div className="text-[13px] text-text2 space-y-1">{secao.conteudo}</div>
            </section>
          ))}
        </div>
      </Modal>
    </>
  )
}

/**
 * A ajuda contextual da 2.4, que mora na BARRA SUPERIOR.
 *
 * `telas/2.4-documentacao.html` e o unico desenho em que "Como ler esta pagina" nao e o
 * botao redondo colado ao titulo: na central de ajuda ele sobe para a barra de cima, com
 * a boia salva-vidas, e abre uma GAVETA a direita em vez do modal central. Faz sentido
 * ali e so ali: a tela inteira ja e ajuda, e um segundo botao de ajuda ao lado do H1
 * disputaria com ela.
 *
 * O shell nao sabe que pagina esta aberta, e nao deve saber. A `Topbar` deixa um vao
 * (`SLOT_DA_AJUDA_DA_PAGINA`) e quem tem ajuda de barra se pendura nele por um portal.
 * O vao e `display: contents`, entao tela nenhuma ganha espaco em branco por ele existir,
 * e nenhuma das outras 45 perde o botao que ja tem.
 *
 * O vao e procurado num EFEITO, e nao durante o render: no primeiro render da pagina o
 * `<div>` da barra ainda nao foi para o DOM, e `getElementById` devolveria `null` para
 * sempre.
 */
export const SLOT_DA_AJUDA_DA_PAGINA = 'dv-ajuda-da-pagina'

export function AjudaNaBarraSuperior({ secoes }: { secoes: readonly SecaoDoInfo[] }) {
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const [vao, setVao] = useState<HTMLElement | null>(null)
  const [aberta, setAberta] = useState(false)

  useEffect(() => {
    setVao(document.getElementById(SLOT_DA_AJUDA_DA_PAGINA))
  }, [])

  if (vao === null) return null

  return createPortal(
    <>
      <button
        type="button"
        onClick={() => {
          setAberta(true)
        }}
        aria-label={texto('ler')}
        title={texto('ler')}
        className="flex items-center gap-2 h-9 px-3 shrink-0 rounded-[10px] bg-white border border-hairline text-[13px] text-text2 hover:border-navy/40"
      >
        <Icone nome="life-buoy" className="w-4 h-4 text-muted" />
        <span className="font-medium hidden lg:inline">{texto('ler')}</span>
      </button>
      <Gaveta
        aberta={aberta}
        titulo={texto('ler')}
        aoFechar={() => {
          setAberta(false)
        }}
      >
        <div className="space-y-5 text-sm">
          {secoes.map((secao) => (
            <section key={secao.titulo}>
              <p className="eyebrow text-muted mb-2">{secao.titulo}</p>
              <div className="text-[13px] text-text2 space-y-1">{secao.conteudo}</div>
            </section>
          ))}
        </div>
      </Gaveta>
    </>,
    vao,
  )
}

/**
 * O atalho para a CENTRAL DE AJUDA na barra superior.
 *
 * A boia salva-vidas que leva a `/documentacao` está em `telas/2.1-inbox.html:88`,
 * `2.2-busca-global.html:79` e `2.3-meu-perfil.html:76`, e só nessas três: em todos os
 * outros desenhos o cabeçalho vai da entidade direto ao sino. Não é a falta de ajuda
 * própria que decide — 3.1, 3.4, 8.7 e 9.1 também não têm "Como ler esta página" e não
 * ganham a boia. O desenho a põe nessas três e em nenhuma outra, e
 * `telas/PROGRESSO.md:304` registra por quê: era a mitigação para a 2.4 ser órfã de
 * navegação.
 *
 * O que NÃO vale concluir daí: nas outras telas o "Como ler esta página" não ocupa este
 * vão. Ali ele é o botão redondo colado ao título, dentro do `main`; a 2.4 é a única
 * exceção, e é por isso que ela mora aqui em cima. E em `8.5-dre.html`, a referência de
 * pixel do `shell.spec.ts`, a barra não tem controle de ajuda nenhum: pendurar o atalho
 * na `Topbar` para as 46 telas empurraria o sino e reprovaria aquela comparação.
 *
 * Por isso ele usa o MESMO vão da `AjudaNaBarraSuperior`, pelo mesmo portal: quem decide
 * é a página, e o shell continua sem saber qual página está aberta. As duas nunca
 * disputam o vão — a 2.4 monta a ajuda de barra, estas três montam o atalho.
 *
 * A barra lateral também leva à `/documentacao` (`shell/sidebar.tsx`), e isso não é um
 * caminho que o desenho não tenha: `telas/dv-shell.js:369` injeta a entrada
 * "Ajuda / Documentação" no fim do nav de TODAS as telas do protótipo.
 */
export function AtalhoDaCentralDeAjuda() {
  const texto = useTextos(TEXTOS_DA_SOBREPOSICAO)
  const [vao, setVao] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setVao(document.getElementById(SLOT_DA_AJUDA_DA_PAGINA))
  }, [])

  if (vao === null) return null

  return createPortal(
    <Link
      to="/documentacao"
      aria-label={texto('ajuda')}
      title={texto('ajuda')}
      className="h-9 w-9 shrink-0 grid place-items-center rounded-[10px] text-text2 hover:bg-navy/5"
    >
      <Icone nome="life-buoy" className="w-[18px] h-[18px]" />
    </Link>,
    vao,
  )
}
